import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../../components/Common/AppOverlay";
import ActionMenu from "../../components/Common/ActionMenu";
import useAdminDataLifecycle from "../../components/Common/useAdminDataLifecycle";
import { showLoading } from "react-global-loading";
import { SuccessNotify, ErrorNotify } from "../../shared/Alert";
import {
  createUpdateLocation,
  getCompanyLocationById,
  getCompanyLocations,
  getCompanySpecificLocations,
} from "../../helpers/backend_helper";
import CustomDataGrid from "../../components/Common/CustomDataGrid";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
};

const BranchActionMenu = ({ item, onOpen, lifecycleActions }) => {
  return (
    <ActionMenu
      label={item.name}
      actions={[
        {
          label: "Edit branch",
          icon: <i className="bx bx-pencil" />,
          onSelect: () => onOpen(item.id),
        },
        {
          label: "View details",
          icon: <i className="bx bx-show" />,
          onSelect: () => onOpen(item.id),
        },
        ...lifecycleActions,
      ]}
    />
  );
};

const gridSx = {
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#ffffff !important",
    borderBottom: "1px solid #e7ebf2",
  },
  "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable": {
    backgroundColor: "#ffffff !important",
    color: "#52627f",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#52627f",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: ".2px",
  },
  "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
  "& .MuiDataGrid-cell": {
    fontSize: 13,
    borderBottomColor: "#edf0f5",
    color: "#273957",
  },
  "& .MuiDataGrid-row": { minHeight: "56px !important" },
  "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
    { fontSize: 14, borderTopColor: "#e7ebf2" },
};

const CompanyLocation = ({ companyId }) => {
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({ id: 0, name: "", address: "" });
  const [submitted, setSubmitted] = useState(false);

  const loadData = () => {
    showLoading(true);
    const request = companyId
      ? getCompanySpecificLocations(companyId)
      : getCompanyLocations();
    return request
      .then((response) => setLocations(response?.data || []))
      .catch((error) => {
        console.error(error);
        ErrorNotify("Unable to load locations.");
      })
      .finally(() => showLoading(false));
  };
  const lifecycle = useAdminDataLifecycle(loadData);

  useEffect(() => {
    loadData();
  }, [companyId]);

  const isActive = (value) =>
    value === 1 || value === true || String(value).toLowerCase() === "active";
  const filteredLocations = useMemo(
    () =>
      locations.filter((location) => {
        const text = `${location.name || ""} ${
          location.address || ""
        }`.toLowerCase();
        return (
          (!search || text.includes(search.toLowerCase())) &&
          (status === "all" ||
            (status === "active"
              ? isActive(location.status)
              : !isActive(location.status)))
        );
      }),
    [locations, search, status]
  );

  const closeDrawer = () => {
    setIsOpen(false);
    setSubmitted(false);
  };

  const addLocation = () => {
    setForm({ id: 0, name: "", address: "" });
    setSubmitted(false);
    setIsOpen(true);
  };

  const editLocation = (id) => {
    showLoading(true);
    getCompanyLocationById(id)
      .then((response) => {
        if (!response?.status)
          throw new Error(
            response?.message || "Unable to load branch details."
          );
        setForm({
          id: response.data.id,
          name: response.data.name || "",
          address: response.data.address || "",
        });
        setSubmitted(false);
        setIsOpen(true);
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify("Unable to load branch details.");
      })
      .finally(() => showLoading(false));
  };

  const saveLocation = (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (!form.name.trim()) return;
    showLoading(true);
    createUpdateLocation({
      id: form.id,
      firmId: companyId,
      name: form.name.trim(),
      address: form.address.trim(),
    })
      .then((response) => {
        if (!response?.status)
          throw new Error(response?.message || "Unable to save branch.");
        SuccessNotify("Branch saved successfully.");
        closeDrawer();
        loadData();
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify("Unable to save branch.");
      })
      .finally(() => showLoading(false));
  };
  const columns = [
    {
      id: "name",
      header: "Branch",
      accessorKey: "name",
      minSize: 220,
      flex: 1,
      cell: ({ row, getValue }) => (
        <button
          type="button"
          className="standards-grid__name"
          style={{ fontSize: 13 }}
          onClick={() => editLocation(row.original.id)}
        >
          <span>{(getValue() || "B").charAt(0)}</span>
          {getValue() || "-"}
        </button>
      ),
    },
    {
      id: "address",
      header: "Address",
      accessorKey: "address",
      minSize: 310,
      flex: 1.8,
      cell: ({ getValue }) => (
        <span className="standards-grid__description">{getValue() || "-"}</span>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => (isActive(row.status) ? "Active" : "Inactive"),
      minSize: 120,
      flex: 0.75,
      cell: ({ row, getValue }) => (
        <span
          className={`standards-grid__status ${
            isActive(row.original.status)
              ? "standards-grid__status--active"
              : "standards-grid__status--archived"
          }`}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "updatedOn",
      header: "Last Updated",
      accessorFn: (row) => formatDate(row.updatedOn),
      minSize: 140,
      flex: 0.8,
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      minSize: 80,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      cell: ({ row }) => (
        <BranchActionMenu
          item={row.original}
          onOpen={editLocation}
          lifecycleActions={lifecycle.actions("location", row.original)}
        />
      ),
    },
  ];

  return (
    <section className="settings-list">
      <header className="settings-list__header">
        <div>
          <h2>Locations</h2>
          <p>Manage the branches and operating locations for your firm.</p>
        </div>
        <button
          type="button"
          className="settings-list__create"
          onClick={addLocation}
        >
          <i className="bx bx-plus" />
          New Branch
        </button>
      </header>
      <section className="settings-list__workspace">
        
        <div className="settings-list__grid">
          <CustomDataGrid
 filterContent={<section className="standards-filter-panel">
          <div className="standards-filter-panel__fields">
            <label>
              <span>Search</span>
              <div className="standards-filter-panel__search">
                <i className="bx bx-search" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search branches..."
                />
              </div>
            </label>
            <label>
              <span>Status</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
          </div>
          <div className="standards-filter-panel__actions">
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatus("all");
              }}
            >Clear</button>
          </div>
        </section>}

            data={filteredLocations}
            columns={columns}
            onRefresh={loadData}
            fileName="locations"
            recordLabel="locations"
            showToolbar={false}
            initialPageSize={15}
            rowHeight={56}
            columnHeaderHeight={56}
            gridSx={gridSx}
          />
        </div>
      </section>
      <Offcanvas
        backdrop="static"
        className="standards-drawer"
        direction="end"
        isOpen={isOpen}
        toggle={closeDrawer}
      >
        <OffcanvasHeader toggle={closeDrawer} className="canvasHeader">
          {form.id ? "Edit Branch" : "New Branch"}
        </OffcanvasHeader>
        <OffcanvasBody>
          <Form className="standard-drawer-form" onSubmit={saveLocation}>
            <div className="standard-drawer-form__content">
              <p className="standard-drawer-form__intro">
                Add the branch details used to organize users and firm activity.
              </p>
              <section className="standard-drawer-form__section">
                <div className="standard-drawer-form__section-title">
                  <span>
                    <i className="bx bx-map" />
                    Branch Information
                  </span>
                </div>
                <FormGroup>
                  <Label for="branch-name">
                    Branch Name <b>*</b>
                  </Label>
                  <Input
                    id="branch-name"
                    value={form.name}
                    onChange={(event) =>
                      setForm({ ...form, name: event.target.value })
                    }
                    invalid={submitted && !form.name.trim()}
                    placeholder="Enter branch name"
                  />
                  {submitted && !form.name.trim() && (
                    <FormFeedback>Branch name is required.</FormFeedback>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label for="branch-address">Address</Label>
                  <Input
                    id="branch-address"
                    type="textarea"
                    rows="5"
                    value={form.address}
                    onChange={(event) =>
                      setForm({ ...form, address: event.target.value })
                    }
                    placeholder="Enter branch address"
                  />
                </FormGroup>
              </section>
            </div>
            <div className="standard-drawer-form__actions">
              <Button
                type="button"
                className="standard-drawer-form__cancel"
                onClick={closeDrawer}
              >
                Cancel
              </Button>
              <Button type="submit" className="standard-drawer-form__submit">
                {form.id ? "Save Changes" : "Create Branch"}
              </Button>
            </div>
          </Form>
        </OffcanvasBody>
      </Offcanvas>
      {lifecycle.dialog}
    </section>
  );
};

export default CompanyLocation;
